import { AnswerSettingMode, FieldClass, RatingType, Scene } from '@/typings/domain'
import { nanoid } from '@reduxjs/toolkit'

type Choice = {
  type: 'Choice'
  name: string
}

type GoodsItem = {
  type: 'GoodsItem'
  name: string
  layout: 'images'
  price: number
  images: { attachmentId?: string }[]
}

type ReservationItem = {
  name: string
  quotaSetting: {
    type: 'by_day_repeat_weekly'
    availableDaysOfWeek: string[]
  }
}

type AnswerBank = {
  type: 'AnswerBank'
  answerSettingMode: AnswerSettingMode
  answers: {
    apiCode: string
    correctAnswer: string
    score: number
  }[]
  enableAnswerExplanation: boolean
}

type DefaultField = {
  position: number
  type: FieldClass
  customizedType?: 'SingleSelect'
  label: string
  ratingType?: RatingType
  ratingMax?: number
  maximumRatingsDisplayText?: string
  minimumRatingsDisplayText?: string
  choices?: Choice[]
  goodsItems?: GoodsItem[]
  reservationItems?: ReservationItem[]
  extraConfigurableSettings?: AnswerBank
}

type DefaultFormWithFieldsType = {
  name: string
  description: string
  fields: DefaultField[]
}

const imageDefaultAttachmentIds = process.env.EXPO_PUBLIC_IMAGE_GOODS_ITEM_ATTACHMENT_IDS?.split(',') || []

export function buildDefaultFormWithFieldsByScene(scene: Scene): DefaultFormWithFieldsType {
  switch (scene) {
    case Scene.Form:
      return buildDefaultFormWithFields()
    case Scene.Survey:
      return buildDefaultSurveyWithFields()
    case Scene.Registry:
      return buildDefaultRegistryWithFields()
    case Scene.OnlinePayment:
      return buildDefaultOnlinePaymentWithFields()
    case Scene.Reservation:
      return buildDefaultReservationWithFields()
    case Scene.Exam:
      return buildDefaultExamWithFields()
    case Scene.Evaluation:
      return buildDefaultEvaluationWithFields()
    case Scene.Vote:
      return buildDefaultFormWithFields()
    case Scene.CustomerAcquisition:
      return buildDefaultFormWithFields()
    default:
      return buildDefaultFormWithFields()
  }
}

function buildDefaultFormWithFields() {
  return {
    name: '未命名表单',
    description: '感谢你能抽出几分钟时间填写以下内容，现在我们马上开始吧！',
    fields: [],
  }
}

function buildDefaultSurveyWithFields() {
  return {
    name: '未命名问卷',
    description: '欢迎参加此次问卷调研，您的想法和建议对我们很重要，仅需要几分钟即可完成填写，谢谢配合。',
    fields: [
      {
        position: 1,
        type: FieldClass.RadioButton,
        label: '您对我们的产品质量和服务是否满意？',
        choices: [
          { type: 'Choice' as const, name: '非常满意' },
          { type: 'Choice' as const, name: '满意' },
          { type: 'Choice' as const, name: '一般' },
          { type: 'Choice' as const, name: '不满意' },
        ],
      },
      {
        position: 2,
        type: FieldClass.RatingField,
        label: '请对我们的在线客户服务做综合打分',
        ratingType: RatingType.Star,
        ratingMax: 5,
        maximumRatingsDisplayText: '非常满意',
        minimumRatingsDisplayText: '非常不满意',
      },
    ],
  }
}

function buildDefaultRegistryWithFields() {
  return {
    name: '未命名报名',
    description:
      '欢迎报名参加我们的活动，请如实填写报名所需信息，便于我们后续联系与安排，您填写的信息将严格保密，谢谢配合。',
    fields: [
      {
        position: 1,
        type: FieldClass.NameField,
        label: '姓名',
      },
      {
        position: 2,
        type: FieldClass.MobileField,
        label: '联系方式',
      },
      {
        position: 3,
        type: FieldClass.AttachmentField,
        label: '上传照片',
      },
    ],
  }
}

function buildDefaultOnlinePaymentWithFields() {
  return {
    name: '未命名收款',
    description: '欢迎采购我们的产品和服务，请选择要购买的内容并完成在线支付。',
    fields: [
      {
        position: 1,
        type: FieldClass.GoodsField,
        label: '配图商品',
        goodsItems: [1, 2, 3].map(index => ({
          type: 'GoodsItem' as const,
          name: `商品 ${index}`,
          layout: 'images' as const,
          price: index * 100,
          images: [{ attachmentId: imageDefaultAttachmentIds[index - 1] }],
        })),
      },
      {
        position: 2,
        type: FieldClass.NameField,
        label: '姓名',
      },
      {
        position: 3,
        type: FieldClass.MobileField,
        label: '联系方式',
      },
      {
        position: 4,
        type: FieldClass.AddressField,
        label: '地址字段',
      },
    ],
  }
}

function buildDefaultReservationWithFields() {
  return {
    name: '未命名预约',
    description: '欢迎使用我们的预约系统，请选择合适的预约时间和项目。',
    fields: [
      {
        position: 1,
        type: FieldClass.ReservationField,
        label: '预约',
        reservationItems: [
          {
            name: '预约项目',
            quotaSetting: {
              type: 'by_day_repeat_weekly' as const,
              availableDaysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
            },
          },
        ],
      },
      {
        position: 2,
        type: FieldClass.NameField,
        label: '姓名',
      },
      {
        position: 3,
        type: FieldClass.MobileField,
        label: '联系方式',
      },
    ],
  }
}

function buildDefaultExamWithFields() {
  const correctAnswerApiCode = nanoid(4)

  return {
    name: '未命名考试',
    description: '欢迎参加本次考试，请认真作答。',
    fields: [
      {
        position: 1,
        type: FieldClass.RadioButton,
        customizedType: 'SingleSelect' as const,
        label: '使用金数据创建在线表单，最常见的用途是下面哪一项？',
        choices: [
          { type: 'Choice' as const, name: '编写手机 App' },
          { type: 'Choice' as const, name: '收集报名、信息或反馈', value: correctAnswerApiCode },
          { type: 'Choice' as const, name: '修图和视频剪辑' },
          { type: 'Choice' as const, name: '玩在线游戏' },
        ],
        extraConfigurableSettings: {
          type: 'AnswerBank' as const,
          answerSettingMode: AnswerSettingMode.Absolute,
          answers: [{ apiCode: nanoid(4), correctAnswer: correctAnswerApiCode, score: 100 }],
          enableAnswerExplanation: false,
        },
      },
    ],
  }
}

function buildDefaultEvaluationWithFields() {
  const answer1ApiCode = nanoid(4)
  const answer2ApiCode = nanoid(4)

  return {
    name: '未命名测评',
    description: '欢迎参加本次测评，请按自己真实想法答题。准备好了吗？我们现在开始吧！',
    fields: [
      {
        position: 1,
        type: FieldClass.NameField,
        label: '姓名',
      },
      {
        position: 2,
        type: FieldClass.RadioButton,
        customizedType: 'SingleSelect' as const,
        label: '出发旅行前，你通常会：',
        choices: [
          { type: 'Choice' as const, name: '提前列好每日行程表和项目', value: answer1ApiCode },
          { type: 'Choice' as const, name: '只订机票和酒店，随心探索', value: answer2ApiCode },
        ],
        extraConfigurableSettings: {
          type: 'AnswerBank' as const,
          answerSettingMode: AnswerSettingMode.Relative,
          answers: [
            { apiCode: nanoid(4), correctAnswer: answer1ApiCode, score: 0 },
            { apiCode: nanoid(4), correctAnswer: answer2ApiCode, score: 0 },
          ],
          enableAnswerExplanation: false,
        },
      },
    ],
  }
}
